/*
 * /classes/:slug/reflections/:subId? — Reflection 4F (Facts · Feelings · Findings · Future).
 * Chưa qua quiz 4F → học 4F trước (LearningFlow). Đã sẵn sàng → viết theo 6 bước,
 * lưu/gửi để Simba chấm (AI) — mentor có thể chấm lại. Bài đang chấm được hỏi lại mỗi 2,5 giây.
 * `subId` = id một reflection: mở sẵn kết quả của bài đó.
 */
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Spinner } from "@/components/ui/spinner"
import { Icon, SemanticIcon } from "@/components/icon"
import { EmptyState, ErrorState, LoadingState, Meter, Page, PageHeader, SectionTitle, StatusNote, usePageTitle } from "@/components/page"
import { LearningModeExit } from "@/components/learning"
import { useClass, useFocusMode } from "@/app/ClassLayout"
import { api, post } from "@/lib/api"
import { formatDisplayDate } from "@/lib/format"
import { cn } from "@/lib/utils"
import { AiEvaluationCard, MentorEvaluationCard } from "./reflections/evaluation"
import { LearningFlow } from "./reflections/learning"
import { contextLabel, emptyReflection, FIELDS, isPending, score100, STATUS_LABEL, type ReflectionForm } from "./reflections/shared"

const LAST_STEP = 5

export default function ReflectionsPage() {
  usePageTitle("Reflections", "Nhìn lại việc học bằng 4F: Facts, Feelings, Findings, Future.")
  const { slug, base } = useClass()
  const { subId } = useParams()
  const navigate = useNavigate()
  const [items, setItems] = useState<any[] | null>(null)
  const [learning, setLearning] = useState<any>()
  const [mode, setMode] = useState<"learn" | "write">("learn")
  const [learnKey, setLearnKey] = useState(0)
  const [form, setForm] = useState<ReflectionForm>(emptyReflection)
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState("")
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null)
  const [error, setError] = useState("")
  const [activeId, setActiveId] = useState(subId || "")
  const root = `/api/classes/${encodeURIComponent(slug)}`

  // Chế độ tập trung: khi đang học 4F hoặc đang ở giữa các bước viết.
  useFocusMode(!!learning && (mode === "learn" || step > 0))

  const load = useCallback(
    () =>
      api(`${root}/reflections`)
        .then((d) => setItems(d.reflections || []))
        .catch((e: any) => setError(e.message)),
    [root],
  )
  const loadLearning = useCallback(() => {
    setError("")
    api(`${root}/reflection-learning`)
      .then((d) => {
        setLearning(d)
        setMode(d.ready_to_reflect ? "write" : "learn")
      })
      .catch((e: any) => setError(e.message))
  }, [root])

  useEffect(() => {
    load()
    loadLearning()
  }, [load, loadLearning])
  useEffect(() => { setActiveId(subId || "") }, [subId])

  // Hỏi lại khi còn bài đang xếp hàng / đang chấm.
  useEffect(() => {
    if (!items?.some((i) => isPending(i.status))) return
    const t = window.setTimeout(load, 2500)
    return () => window.clearTimeout(t)
  }, [items, load])

  const update = (key: keyof ReflectionForm, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const save = async (action: "draft" | "submit") => {
    setBusy(action)
    setMessage(null)
    try {
      const result = await post(`${root}/reflections`, { ...form, action })
      const reward = result.rewards || {}
      const earned = Number(reward.points_earned || 0)
      const badges: string[] = Array.isArray(reward.badges_awarded) ? reward.badges_awarded : []
      const rewardText = [earned ? `+${earned} XP` : null, badges.length ? `Mở khóa badge: ${badges.join(", ")}` : null].filter(Boolean).join(" · ")
      setMessage({
        tone: "success",
        text: `${action === "draft" ? "Đã lưu phiên bản" : "Đã gửi bài"}. Simba đang chấm và sẽ đưa 3 câu hỏi giúp con nâng cấp bài.${rewardText ? ` ${rewardText}.` : ""}`,
      })
      setForm((f) => ({ ...f, id: result.id }))
      setActiveId(result.id)
      setStep(0)
      await load()
    } catch (e: any) {
      setMessage({ tone: "error", text: e.message })
    } finally {
      setBusy("")
    }
  }

  const edit = (item: any) => {
    if (!learning?.ready_to_reflect) {
      setLearnKey((k) => k + 1)
      setMode("learn")
      return
    }
    setForm({
      id: item.id,
      reflection_date: item.reflection_date,
      starts_at: item.starts_at || "",
      ends_at: item.ends_at || "",
      learning_context: item.learning_context || "class_session",
      facts: item.facts || "",
      feelings: item.feelings || "",
      findings: item.findings || "",
      future: item.future || "",
    })
    setMessage(null)
    setStep(1)
    setMode("write")
    if (subId) navigate(`${base}/reflections`)
    scrollTo({ top: 0, behavior: "smooth" })
  }

  const startNew = () => {
    setForm(emptyReflection())
    setActiveId("")
    setMessage(null)
    setStep(0)
  }

  if (error && !learning) return <ErrorState message={error} onRetry={loadLearning} />
  if (!learning) return <LoadingState label="Đang chuẩn bị 4F…" />

  if (mode === "learn")
    return (
      <Page className="max-w-[760px]">
        <div>
          <LearningModeExit slug={slug} to={learning.ready_to_reflect ? undefined : `${base}`} />
        </div>
        <LearningFlow
          key={learnKey}
          slug={slug}
          learning={learning}
          onLearning={setLearning}
          onWrite={() => {
            setStep(0)
            setMode("write")
          }}
        />
      </Page>
    )

  const active = items?.find((i) => i.id === activeId)
  const writing = step > 0

  return (
    <Page className={cn(writing && "max-w-[760px]")}>
      {writing ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" size="sm" onClick={() => setStep(0)}>
            <Icon name="arrow-left" className="size-4" /> Thoát chế độ viết
          </Button>
          <span className="text-sm text-muted-foreground">{form.id ? "Đang nâng cấp bài" : "Bài mới"}</span>
        </div>
      ) : (
        <PageHeader
          eyebrow="Reflection 4F"
          title={<>Nhìn lại để <em className="text-orange not-italic">tiến lên</em></>}
          description="Viết 4 chữ F về một buổi học hoặc lúc con tự học. Simba đọc bài và gợi ý cách nâng cấp."
          actions={
            <Button
              variant="outline"
              onClick={() => {
                setLearnKey((k) => k + 1)
                setMode("learn")
              }}
            >
              <Icon name="book-open" className="size-4" /> Ôn lại 4F
            </Button>
          }
        />
      )}

      <Wizard form={form} step={step} setStep={setStep} update={update} busy={busy} onSave={save} onReset={startNew} />
      {message && <StatusNote tone={message.tone}>{message.text}</StatusNote>}

      {!writing && activeId && (
        <section aria-live="polite" className="flex flex-col gap-4">
          <SectionTitle title="Kết quả bài vừa chọn" />
          {active?.status === "evaluated" ? (
            <>
              <AiEvaluationCard item={active} onImprove={() => edit(active)} />
              {active.mentor_evaluation && <MentorEvaluationCard evaluation={active.mentor_evaluation} />}
            </>
          ) : active?.status === "failed" ? (
            <Card size="sm" className="border-danger/30">
              <CardContent className="flex flex-wrap items-center gap-4">
                <span className="grid size-12 place-items-center rounded-2xl bg-danger-soft">
                  <Icon name="bell" className="size-6 text-danger" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-lg font-bold text-ink">Chưa chấm được lần này</p>
                  <p className="text-sm text-muted-foreground">Con vẫn có thể mở bài và lưu lại để Simba chấm lại.</p>
                </div>
                <Button variant="outline" onClick={() => edit(active)}>
                  Mở bài
                </Button>
              </CardContent>
            </Card>
          ) : active && active.status === "draft" ? null : (
            <Card size="sm">
              <CardContent className="flex items-center gap-4">
                <span className="grid size-12 place-items-center rounded-2xl bg-soft">
                  <Spinner className="size-6 text-orange" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-lg font-bold text-ink">Simba đang đọc bài…</p>
                  <p className="text-sm text-muted-foreground">Kết quả và 3 câu hỏi nâng cấp sẽ xuất hiện ở đây.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {!writing && (
        <History
          items={items}
          activeId={activeId}
          onOpen={(id) => {
            setActiveId(id)
            navigate(`${base}/reflections/${encodeURIComponent(id)}`, { replace: true })
          }}
          onEdit={edit}
          error={error}
          onRetry={load}
        />
      )}
    </Page>
  )
}

/* ---------------- Viết bài: bước 0 bối cảnh, 1–4 bốn chữ F, 5 xem lại ---------------- */

function Wizard({
  form,
  step,
  setStep,
  update,
  busy,
  onSave,
  onReset,
}: {
  form: ReflectionForm
  step: number
  setStep: (fn: (s: number) => number) => void
  update: (key: keyof ReflectionForm, value: string) => void
  busy: string
  onSave: (action: "draft" | "submit") => void
  onReset: () => void
}) {
  const field = FIELDS[step - 1]
  const title = step === 0 ? "Khoảng thời gian" : step === LAST_STEP ? "Gửi bài?" : `${step}/4 · ${field.title}`
  return (
    <Card>
      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold tracking-[0.08em] text-orange uppercase">Viết Reflection</p>
            {form.id && step === 0 && (
              <Button variant="ghost" size="sm" onClick={onReset}>
                <Icon name="plus" className="size-4" /> Bài mới
              </Button>
            )}
          </div>
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-ink">
            {field && <SemanticIcon label={field.title} className="size-6 text-orange" />}
            {title}
          </h2>
          <Meter value={Math.max(8, (step / LAST_STEP) * 100)} />
          <ol className="hidden gap-1.5 sm:flex" aria-label="Các bước">
            {["Bối cảnh", ...FIELDS.map((f) => f.title), "Gửi"].map((label, i) => (
              <li key={label}>
                <button
                  type="button"
                  onClick={() => setStep(() => i)}
                  aria-current={i === step ? "step" : undefined}
                  className={cn(
                    "rounded-full px-3 py-1 text-sm transition-colors",
                    i === step ? "bg-orange text-white" : i < step ? "bg-soft text-accent-foreground" : "text-muted-foreground hover:bg-soft",
                  )}
                >
                  {label}
                </button>
              </li>
            ))}
          </ol>
        </div>

        {step === 0 && (
          <div className="flex flex-col gap-5">
            <h3 className="text-lg font-semibold text-ink">Con muốn nhìn lại điều gì?</h3>
            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  ["class_session", "Buổi học", "graduation"],
                  ["self_study", "Tự học", "book-open"],
                ] as const
              ).map(([value, label, icon]) => {
                const on = form.learning_context === value
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => update("learning_context", value)}
                    className={cn(
                      "flex flex-col items-center gap-2 rounded-2xl border-[1.5px] px-3 py-4 transition-colors",
                      on ? "border-orange bg-soft text-ink" : "border-edge bg-card text-muted-foreground hover:border-orange/60",
                    )}
                  >
                    <Icon name={icon} className={cn("size-7", on && "text-orange")} />
                    <strong className="font-heading text-base">{label}</strong>
                  </button>
                )
              })}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rf-date">Ngày</Label>
                <Input id="rf-date" type="date" value={form.reflection_date} onChange={(e) => update("reflection_date", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:col-span-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rf-start">Bắt đầu</Label>
                  <Input id="rf-start" type="time" value={form.starts_at} onChange={(e) => update("starts_at", e.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rf-end">Kết thúc</Label>
                  <Input id="rf-end" type="time" value={form.ends_at} onChange={(e) => update("ends_at", e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        )}

        {field && (
          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold text-ink">{field.prompt}</h3>
            <p className="flex gap-2 rounded-2xl bg-sand px-4 py-3 text-sm text-muted-foreground">
              <b className="shrink-0 text-ink">Ví dụ</b>
              {field.example}
            </p>
            <Textarea
              key={field.key}
              autoFocus
              rows={6}
              value={form[field.key]}
              onChange={(e) => update(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="min-h-40 text-base"
            />
          </div>
        )}

        {step === LAST_STEP && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <img src="/brand/sutucon/mascots-2d/elephant-happy.svg" alt="" className="size-16" />
              <h3 className="font-heading text-xl font-bold text-ink">Sẵn sàng rồi!</h3>
            </div>
            <ReviewList item={form} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-5">
          {step > 0 && (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
              <Icon name="arrow-left" className="size-4" /> Quay lại
            </Button>
          )}
          <span className="flex-1" />
          <Button variant="outline" disabled={!!busy} onClick={() => onSave("draft")}>
            {busy === "draft" ? "Đang lưu…" : "Lưu & chấm"}
          </Button>
          {step < LAST_STEP ? (
            <Button onClick={() => setStep((s) => s + 1)}>
              Tiếp <Icon name="arrow-right" className="size-4" />
            </Button>
          ) : (
            <Button disabled={!!busy} onClick={() => onSave("submit")}>
              <Icon name="send" className="size-4" /> {busy === "submit" ? "Đang gửi…" : "Gửi bài"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ReviewList({ item }: { item: any }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {FIELDS.map((f) => (
        <div key={f.key} className="flex flex-col gap-1 rounded-2xl bg-sand px-4 py-3">
          <dt className="flex items-center gap-2 text-sm font-semibold text-ink">
            <SemanticIcon label={f.title} className="size-4 text-orange" />
            {f.title}
          </dt>
          <dd className={cn("text-sm break-words whitespace-pre-line", item[f.key] ? "text-ink" : "text-muted-foreground italic")}>{item[f.key] || "Chưa viết"}</dd>
        </div>
      ))}
    </dl>
  )
}

/* ---------------- Bài trước ---------------- */

function History({
  items,
  activeId,
  onOpen,
  onEdit,
  error,
  onRetry,
}: {
  items: any[] | null
  activeId: string
  onOpen: (id: string) => void
  onEdit: (item: any) => void
  error: string
  onRetry: () => void
}) {
  return (
    <section className="flex flex-col gap-4">
      <SectionTitle title={`Bài trước${items ? ` (${items.length})` : ""}`} description="Mở một bài để xem nhận xét của Simba và mentor." />
      {error && <ErrorState message={error} onRetry={onRetry} />}
      {!items && !error && <LoadingState />}
      {items && !items.length && <EmptyState mascot="elephant-thinking" title="Chưa có bài" description="Bài reflection đầu tiên của con sẽ nằm ở đây." />}
      {items && items.length > 0 && (
        <ul className="flex flex-col gap-3">
          {items.map((item) => (
            <li key={item.id}>
              <HistoryItem item={item} highlighted={item.id === activeId} onOpen={() => onOpen(item.id)} onEdit={() => onEdit(item)} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function statusBadge(item: any) {
  if (item.completion_status === "completed") return <Badge variant="success">Hoàn thành</Badge>
  if (item.status === "failed") return <Badge variant="destructive">{STATUS_LABEL.failed}</Badge>
  if (isPending(item.status))
    return (
      <Badge variant="warning">
        <Spinner className="size-3.5" /> {STATUS_LABEL[item.status]}
      </Badge>
    )
  return <Badge variant={item.status === "evaluated" ? "default" : "secondary"}>{STATUS_LABEL[item.status] || item.status}</Badge>
}

function HistoryItem({ item, highlighted, onOpen, onEdit }: { item: any; highlighted: boolean; onOpen: () => void; onEdit: () => void }) {
  const assessments: any[] = Array.isArray(item.assessments) ? item.assessments : []
  return (
    <Collapsible defaultOpen={highlighted} onOpenChange={(open) => open && onOpen()}>
      <Card size="sm" className={cn(highlighted && "border-orange")}>
        <CollapsibleTrigger className="group flex w-full items-center gap-3 px-4 text-left">
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-soft">
            <Icon name={item.learning_context === "self_study" ? "book-open" : "graduation"} className="size-5 text-orange" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="truncate font-heading text-base font-semibold text-ink">
              {contextLabel(item.learning_context)} · {formatDisplayDate(item.reflection_date)}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              {statusBadge(item)}
              {item.mentor_evaluation && <Badge variant="outline">Mentor đã chấm</Badge>}
            </span>
          </span>
          {item.normalized_score != null && <b className="shrink-0 font-heading text-lg text-ink">{score100(item.normalized_score)}<span className="text-sm text-muted-foreground">/100</span></b>}
          <Icon name="chevron-down" className="size-5 shrink-0 transition-transform group-data-[panel-open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-4 border-t border-line px-4 pt-4">
            {item.status !== "evaluated" && item.overall_feedback && (
              <p className="rounded-2xl bg-soft px-4 py-3 text-sm text-ink">
                <b className="block">Gợi ý của Simba</b>
                {item.overall_feedback}
              </p>
            )}
            {item.completion_status !== "completed" && !isPending(item.status) && (
              <div>
                <Button size="sm" onClick={onEdit}>
                  <Icon name="sparkles" className="size-4" /> Nâng cấp bài
                </Button>
              </div>
            )}
            {item.status === "evaluated" && <AiEvaluationCard item={item} onImprove={onEdit} compact />}
            {item.mentor_evaluation && <MentorEvaluationCard evaluation={item.mentor_evaluation} compact />}
            {assessments.length > 1 && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold text-ink">Lịch sử {assessments.length} lần chấm</p>
                <ol className="flex flex-col gap-2">
                  {assessments.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl bg-sand px-3 py-2 text-sm">
                      <b className="text-ink">Lần {a.revision}</b>
                      <strong className="text-orange-strong">{score100(a.normalized_score)}/100</strong>
                      <span className="min-w-0 basis-full text-muted-foreground sm:basis-auto sm:flex-1">{a.overall_feedback}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-ink">Bài của con</p>
              <ReviewList item={item} />
            </div>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
